import { useState } from "react";
import { ConfirmModalSecondaryAction } from "../../ConfirmModal";

interface UseCatalogueDeleteOptions {
  /**
   * Deletes the record. Resolving closes the dialog; rejecting keeps it open so the reader
   * can retry or switch the record off instead.
   */
  deleteRecord: () => Promise<void>;
  /** Switches the record off, closing the dialog under the same rule as `deleteRecord`. */
  deactivateRecord: () => Promise<void>;
  /** The softer alternative's button label, e.g. "ปิดสินค้าแทน". */
  deactivateLabel: string;
  /** Switching off is only an alternative while the record is still on. */
  canDeactivate: boolean;
  onClose: () => void;
}

interface CatalogueDeleteFlow {
  deleting: boolean;
  /** Undefined hides the alternative's button. */
  deactivateAction?: ConfirmModalSecondaryAction;
  handleClose: () => void;
  handleDelete: () => Promise<void>;
}

/** Answers whether the request went through. */
const didRequestSucceed = async (
  request: () => Promise<void>,
): Promise<boolean> => {
  try {
    await request();
    return true;
  } catch {
    // The axios interceptor already reported the failure; the caller keeps the dialog open.
    return false;
  }
};

/**
 * The request side of a type-to-confirm catalogue delete, shared by the product and
 * pricebook dialogs: either request locks the dialog while it runs, and closes it once
 * it went through.
 */
export const useCatalogueDelete = ({
  deleteRecord,
  deactivateRecord,
  deactivateLabel,
  canDeactivate,
  onClose,
}: UseCatalogueDeleteOptions): CatalogueDeleteFlow => {
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deactivating, setDeactivating] = useState<boolean>(false);

  const handleClose = () => {
    if (deleting || deactivating) return;
    onClose();
  };

  const runThenClose = async (
    request: () => Promise<void>,
    setRunning: (running: boolean) => void,
  ) => {
    setRunning(true);
    const succeeded = await didRequestSucceed(request);
    setRunning(false);
    if (succeeded) onClose();
  };

  const handleDelete = () => runThenClose(deleteRecord, setDeleting);

  const handleDeactivate = () => runThenClose(deactivateRecord, setDeactivating);

  const deactivateAction = canDeactivate
    ? {
        label: deactivateLabel,
        onClick: handleDeactivate,
        loading: deactivating,
      }
    : undefined;

  return { deleting, deactivateAction, handleClose, handleDelete };
};
